// Komponen cetak struk thermal (58mm / 80mm) — layout ringkas ala kasir.
// Dipakai untuk template jenis "struk" ATAU ukuran_kertas thermal_* (lihat
// `isStruk` di FakturPrint.jsx, satu sumber untuk keputusan pemilihan
// komponen dipakai bersama oleh FakturSettings & FakturPublik).
//
// Semua data datang dari props — komponen ini TIDAK pernah fetch sendiri.
import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import WarrantyTerms from "./WarrantyTerms";
import { rp, cetak, normalisasiEntitas, normalisasiItems } from "./FakturPrint";
import { rincianPenjualanCetak, labelTempo } from "../../lib/notaPenjualan";

const LEBAR_MM = { thermal_58: 58, thermal_80: 80 };

export default function StrukPrint({
  data, template, publik = false, noFaktur, csatLink, garansi, tampilkanTombolCetak = true,
}) {
  const entity = normalisasiEntitas(data);
  const tpl = template || data?.template || {};
  const opsi = tpl.opsi || {};
  const kop = tpl.kop || {};
  const ukuran = tpl.ukuran_kertas || "thermal_80";
  const lebar = LEBAR_MM[ukuran] || 80;
  const items = normalisasiItems(entity);
  const noFakturFinal = noFaktur || data?.no_faktur || entity.code || "-";
  const csat = csatLink || data?.csat_link || null;
  const tampilSku = opsi.tampil_sku !== false;
  const tampilImei = opsi.tampil_imei !== false;
  // Kontrak B4 — sama seperti FakturPrint.jsx, bawaan TAMPIL.
  const tampilKomponenBundle = opsi.tampil_komponen_bundle !== false;

  // Logo kop — struk sempit (58/80mm) jadi ukurannya dibatasi tinggi saja
  // (biar lebar menyesuaikan proporsi gambar). Sebelumnya struk tidak punya
  // logo sama sekali; kosong/gagal dimuat = tetap tanpa logo (perilaku lama).
  const [logoGagal, setLogoGagal] = useState(false);
  useEffect(() => { setLogoGagal(false); }, [kop.logo_url]);
  const tampilkanLogo = kop.tampil_logo !== false && !!kop.logo_url && !logoGagal;

  // PPN (keputusan owner 18 Agu 2026) — sama seperti FakturPrint.jsx: struk
  // ber-PPN mencetak DPP & PPN terpisah, dan TOTAL adalah DPP+PPN.
  const dppDokumen = entity.dpp ?? entity.total_harga ?? 0;
  const ppnDokumen = entity.ppn_nominal ?? 0;
  // Penjualan dari server baru: TOTAL = nilai nota (brief 22 Sep B3), satu
  // sumber rincian dengan FakturPrint (`rincianPenjualanCetak`).
  const rincianJual = rincianPenjualanCetak(entity);
  const totalDokumen = rincianJual
    ? rincianJual.total
    : entity.kena_ppn
      ? dppDokumen + ppnDokumen
      : (entity.total_harga ?? entity.nilai_total ?? entity.harga_terima
        ?? items.reduce((a, it) => a + it.total, 0));

  return (
    <div className="w-full">
      {/* Roll length comes from the printer. Mixed length + auto is invalid
          CSS and silently retained the global A4 page, shrinking receipts. */}
      <style>{`@media print { @page { size: auto; margin: 3mm; } }`}</style>

      {tampilkanTombolCetak && (
        <div className="no-print flex justify-end mb-3">
          <button type="button" onClick={cetak} data-testid="btn-cetak-struk"
            className="inline-flex items-center gap-2 h-9 px-4 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50">
            <Printer className="w-4 h-4" /> Cetak
          </button>
        </div>
      )}

      <div
        data-testid="struk-print-area"
        style={{ width: `${lebar}mm` }}
        className="print-area mx-auto bg-white border border-slate-200 rounded-xl shadow-sm p-3 font-mono text-[11px] leading-snug text-slate-900"
      >
        <div className="text-center mb-2">
          {tampilkanLogo && (
            <img
              src={kop.logo_url}
              alt={kop.judul || "Logo"}
              data-testid="struk-logo-gambar"
              onError={() => setLogoGagal(true)}
              className="mx-auto mb-1 object-contain"
              style={{ maxHeight: "12mm", maxWidth: "80%" }}
            />
          )}
          <p className="font-bold text-sm">{kop.judul || "STRUK PENJUALAN"}</p>
          {kop.alamat && <p className="text-[10px] text-slate-600">{kop.alamat}</p>}
          {kop.telepon && <p className="text-[10px] text-slate-600">Telp: {kop.telepon}</p>}
        </div>

        <div className="border-t border-dashed border-slate-400 my-1.5" />

        <div className="flex justify-between text-[10px]">
          <span data-testid="struk-no-dokumen">{noFakturFinal}</span>
          <span>{entity.tanggal || "-"}{entity.waktu ? ` ${entity.waktu}` : ""}</span>
        </div>
        {entity.nama_pelanggan && <p className="text-[10px]">Plg: {entity.nama_pelanggan}</p>}
        {entity.nama_penjual && <p className="text-[10px]">Kasir: {entity.nama_penjual}</p>}

        <div className="border-t border-dashed border-slate-400 my-1.5" />

        {items.length === 0 ? (
          <p className="text-center text-slate-400 py-2">Tidak ada barang</p>
        ) : items.map((it) => (
          <div key={it.key} className="mb-1" data-testid={`struk-item-${it.key}`}>
            <p className="truncate">
              {it.nama}
              {tampilImei && it.imeiList.length > 0 ? ` (${it.imeiList[0]})` : ""}
            </p>
            <div className="flex justify-between text-[10px] text-slate-600">
              <span>{it.qty} x {rp(it.harga)}{tampilSku && it.sku ? ` · ${it.sku}` : ""}</span>
              <span>{rp(it.total)}</span>
            </div>
            {/* Diskon produk per baris (D5) */}
            {it.diskon > 0 && (
              <div className="flex justify-between text-[10px] text-slate-600" data-testid={`struk-item-diskon-${it.key}`}>
                <span>  diskon</span><span>-{rp(it.diskon)}</span>
              </div>
            )}
            {/* Sub-baris komponen paket kit (kontrak B4) — tanpa harga per komponen. */}
            {tampilKomponenBundle && it.komponen.length > 0 && (
              <div data-testid={`struk-item-${it.key}-komponen`}>
                {it.komponen.map((k, ki) => (
                  <p key={ki} className="text-[10px] text-slate-500 truncate">
                    - {k.qty}× {k.nama}{tampilImei && k.imei ? ` [${k.imei}]` : ""}
                  </p>
                ))}
              </div>
            )}
          </div>
        ))}

        <div className="border-t border-dashed border-slate-400 my-1.5" />

        {rincianJual && (rincianJual.diskon > 0 || rincianJual.biayaAdmin > 0) && (
          <div className="text-[10px] text-slate-600" data-testid="struk-rincian-diskon">
            <div className="flex justify-between"><span>Subtotal</span><span>{rp(rincianJual.subtotal)}</span></div>
            {rincianJual.diskon > 0 && (
              <div className="flex justify-between"><span>Diskon</span><span data-testid="struk-diskon">-{rp(rincianJual.diskon)}</span></div>
            )}
            {rincianJual.biayaAdmin > 0 && (
              <div className="flex justify-between"><span>Biaya admin</span><span>{rp(rincianJual.biayaAdmin)}</span></div>
            )}
          </div>
        )}
        {entity.kena_ppn && (
          <div className="text-[10px] text-slate-600" data-testid="struk-ppn-breakdown">
            <div className="flex justify-between"><span>DPP</span><span>{rp(dppDokumen)}</span></div>
            <div className="flex justify-between">
              <span>PPN ({entity.ppn_persen ?? 11}%)</span>
              <span data-testid="struk-ppn-nominal">{rp(ppnDokumen)}</span>
            </div>
          </div>
        )}
        <div className="flex justify-between font-bold text-sm">
          <span>TOTAL</span>
          <span data-testid="struk-total">{rp(totalDokumen)}</span>
        </div>

        {rincianJual ? (
          // Penjualan (D4/D5/D7): baris metode + keterangan, trade-in sebagai
          // pembayaran, DP, lalu sisa tagihan nota bertempo.
          <div className="mt-1" data-testid="struk-pembayaran">
            {rincianJual.pembayaran.map((p, i) => (
              <div key={i} className="text-[10px] text-slate-600">
                <div className="flex justify-between gap-2">
                  <span className="truncate">{p.label}</span><span>{rp(p.nilai)}</span>
                </div>
                {p.keterangan && <p className="truncate pl-2">{p.keterangan}</p>}
              </div>
            ))}
            {rincianJual.metodeTunggal && (
              <div className="flex justify-between text-[10px] text-slate-600">
                <span>Metode</span><span>{rincianJual.metodeTunggal}</span>
              </div>
            )}
            {rincianJual.tempo && (
              <div className="flex justify-between text-[10px] font-bold mt-0.5" data-testid="struk-sisa">
                <span>SISA (Tempo {labelTempo(rincianJual.tempo)})</span>
                <span>{rincianJual.sisa === null ? "-" : rp(rincianJual.sisa)}</span>
              </div>
            )}
            {rincianJual.tempo && rincianJual.jatuhTempo && (
              <p className="text-[10px] text-slate-600">Jatuh tempo {rincianJual.jatuhTempo}</p>
            )}
          </div>
        ) : entity.payments && entity.payments.length > 0 && (
          <div className="mt-1">
            {entity.payments.map((p, i) => (
              <div key={i} className="flex justify-between text-[10px] text-slate-600">
                <span>{p.method}</span><span>{rp(p.amount)}</span>
              </div>
            ))}
          </div>
        )}

        {opsi.tampil_catatan !== false && entity.catatan && (
          <p className="text-[10px] text-slate-600 mt-1.5">{entity.catatan}</p>
        )}

        {/* Catatan penjual — tetap milik TEMPLATE, berdampingan dengan catatan
            transaksi di atas (bukan menggantikannya). */}
        {tpl.catatan_penjual && tpl.catatan_penjual.trim() && (
          <p className="text-[10px] text-slate-600 mt-1.5">{tpl.catatan_penjual}</p>
        )}

        {/* Tautan CSAT — QR dibuat backend (csat.qr_data_url), TIDAK memuat
            dari layanan pihak ketiga di internet. Kalau backend belum sempat
            membuatkan QR, jangan sembunyikan diam-diam — tetap tampilkan
            tautannya sebagai teks. */}
        {opsi.sertakan_link_csat && csat?.survey_url && (
          <>
            <div className="border-t border-dashed border-slate-400 my-1.5" />
            <p className="text-center text-[10px]">Nilai pelayanan kami:</p>
            {csat.qr_data_url ? (
              <img
                src={csat.qr_data_url}
                alt="QR penilaian pelayanan" data-testid="struk-csat-qr"
                className="block mx-auto my-1"
                style={{ width: "22mm", height: "22mm" }}
              />
            ) : (
              <p className="text-center text-[9px] text-slate-500">(Kode QR belum tersedia)</p>
            )}
            <p className="text-center text-[9px] break-all">{csat.survey_url}</p>
          </>
        )}

        <WarrantyTerms warranty={garansi || data?.garansi} thermal />
        {tpl.footer_teks && (
          <>
            <div className="border-t border-dashed border-slate-400 my-1.5" />
            <p className="text-center text-[10px]">{tpl.footer_teks}</p>
          </>
        )}

        {/* Syarat & Ketentuan — blok terpisah dari footer_teks, di bagian
            paling bawah struk (fine print). Thermal panjangnya tak terbatas
            jadi cukup diberi jarak & garis putus-putus, tanpa perlu dipotong. */}
        {tpl.syarat_ketentuan && tpl.syarat_ketentuan.trim() && (
          <>
            <div className="border-t border-dashed border-slate-400 my-1.5" />
            <p className="text-center text-[9px] font-semibold text-slate-600">Syarat &amp; Ketentuan</p>
            <p className="text-[9px] text-slate-500 whitespace-pre-line">{tpl.syarat_ketentuan}</p>
          </>
        )}

        {publik && (
          <p className="text-center text-[9px] text-slate-300 mt-1.5">Dokumen dibuat otomatis oleh sistem.</p>
        )}
      </div>
    </div>
  );
}
